<template>
  <div class="profile-container">
    <div class="page-header-container">
      <div class="page-header">
        <span class="page-title">{{ $t("user.profile.title") }}</span>
        <FormActionBtns
          :hiddenAdd="true"
          :onCancel="cancel"
          :onSave="saveProfile"
          v-model="status"
          class="action-btns"
        />
      </div>
    </div>
    <div class="profile-main">
      <div class="profile-basic">
        <div class="profile-section-title">{{ $t("user.profile.basicInformation") }}</div>
        <div class="profile-item">
          <label class="emphasis">{{ $t('username') }}</label>
          <span class="label-content">{{ pendingUpdateUser.username }}</span>
        </div>
        <div class="profile-item">
          <label class="emphasis">
            {{ $t('user.email') }}
            <span class="label-tip">{{ $t('user.profile.2faOnly') }}</span>
          </label>
          <InputField
            :extendClass="isValidEmail? '': 'error-highlight'"
            :readonly="!isEdit"
            :showFlag="false"
            :showColon="false"
            :value="primaryContactEmail"
            @updateValue="updateContactEmail"
          />
        </div>
        <div class="profile-item">
          <label class="emphasis">
            {{ $t('user.phone') }}
            <span class="label-tip">{{ $t('user.profile.2faOnly') }}</span>
          </label>
          <div class="phone-field">
            <span
              class="label-content"
              v-if="!isEdit"
            >{{ currentNationalCode && currentNationalCode.dial_code }}</span>
            <div
              class="national-code__selector"
              v-else
            >
              <HKLSelect
                :value="selectedNationalCodeValue"
                :options="nationalCodeHklOptions"
                :showSearch="true"
                :placeholder="$t('user.countryCode')"
                @change="onNationalCodeChange"
              />
            </div>
            <div class="phone-number__input">
              <InputField
                :extendClass="isValidPhone ? '' : 'error-highlight'"
                :readonly="!isEdit"
                :showFlag="false"
                :showColon="false"
                :value="currentPhoneNumber"
                @updateValue="updatePhoneNumber"
              />
            </div>
          </div>
        </div>
        <div
          class="profile-item"
          v-if="mainContactsInfo"
        >
          <label class="emphasis">
            {{ $t('common.remarks') }}
          </label>
          <span class="label-content">{{ $t('user.profile.remarkContent', [mainContactsInfo]) }}</span>
        </div>
      </div>
      <div class="profile-account-and-dashboard">
        <div class="profile-account">
          <template v-if="!globalOptionsReady">
            {{ $t('loading') }}
          </template>
          <template v-else>
            <div class="profile-section-title">{{ $t("user.profile.accountInformation") }}</div>
            <div class="profile-item">
              <label class="emphasis">{{ $t('user.profile.defaultLanguage') }}</label>
              <span
                class="label-content"
                v-if="!isEdit || languageOptions.length <= 1"
              >{{ customLangLabel(selectedLanguage) }}</span>
              <HKLSelect
                v-else
                :value="selectedLanguageId"
                :options="languageHklOptions"
                :showSearch="true"
                placeholder=""
                @change="onLanguageChange"
              />
            </div>
          </template>
        </div>
        <div class="profile-dashboard">
          <div class="profile-section-title">{{ $t("user.profile.passwordInformation") }}</div>
          <div class="profile-section-content">
            <div class="profile-item">
              <label class="emphasis">{{ $t('user.profile.resetPassword') }}</label>
              <label class="emphasis">{{ '*'.repeat(10) }}</label>
            </div>
            <div class="profile-item">
              <button
                class="btn btn--darkblue"
                @click="resetPassword()"
              >{{ $t("action.requestToResetPassword") }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import lodash from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import notification from '@/utils/notification'
import {getUserEmail,
  getUserProfileEmail, getUserProfilePhoneNumber, updateUserProfileEmail, updateUserProfilePhoneNumber,
  getUserProfileName,
} from '@/api/users/util'
import { utils as formUtils } from '@/plugin/InputChange'
import { getExternalUserProfile, updateExternalUserProfile, resetExternalUserPassword } from '@/api/users/request'
import { userTypeMapping } from '@/utils/locale'
import * as regex from '@/utils/regex'
import FormActionBtns from '@/controls/FormActionBtns'
import InputField from '@/controls/InputField'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import countries from '../constants/countries.json'

const phoneNumberSeparator = '|'

const getDefaultProfilePhoneNumber = (user) => {
  const value = getUserProfilePhoneNumber(user)
  const splitedValue = lodash.split(value, phoneNumberSeparator)
  let nationalCode = {}
  let phoneNumber = ''
  if (splitedValue && splitedValue.length >= 2) {
    const nationalCodeString = lodash.get(splitedValue, [0])
    nationalCode = lodash.find(countries, (country) => lodash.isEqual(lodash.get(country, ['dial_code']), nationalCodeString))
    phoneNumber = lodash.join(lodash.slice(splitedValue, 1), '')
  } else {
    phoneNumber = lodash.join(splitedValue, '')
  }

  const result = {
    nationalCode,
    phoneNumber,
  }
  return result
}

const getFullPhoneNumber = (nationalCode, phoneNumber) => {
  return lodash.join([nationalCode, phoneNumber], phoneNumberSeparator)
}

export default {
  components: {
    FormActionBtns,
    InputField,
    HKLSelect,
  },
  data () {
    return {
      pendingUpdateUser: {},
      profileData: {},
      nationalCodeOptions: countries,
      currentNationalCode: undefined,
      currentPhoneNumber: '',
      status: 'view',
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'userRegions', 'currentGlobalUser', 'isContractor']),
    ...mapGetters('language', ['regionLanguages', 'currentUserDefaultLang']),
    userType () {
      return userTypeMapping[this.currentUser.usertype]
    },
    primaryContactEmail () {
      const result = getUserProfileEmail(this.pendingUpdateUser)
      return result
    },
    primaryContactPhoneNumber () {
      const result = getDefaultProfilePhoneNumber(this.pendingUpdateUser)
      return result
    },
    mainContactsInfo () {
      const adminUsers = this.pendingUpdateUser.adminUsers || []
      let adminLists = []
      if (adminUsers.length) {
        adminUsers.map(user => {
          const name = getUserProfileName(user) || lodash.get(user, ['displayName'], '')
          const email = getUserEmail(user) || lodash.get(user, ['username'], '')
          const adminInfo = `${name} (${email})`
          adminLists.push(adminInfo)
        })
      }
      return adminLists.join(`${this.$t('andWithSpace')}`)
    },
    globalOptionsReady () {
      let result
      if (lodash.isArray(this.languageOptions) && this.languageOptions.length > 0) {
        result = true
      } else {
        result = false
      }
      return result
    },
    languageOptions () {
      return this.regionLanguages
    },
    selectedLanguage () {
      let result
      if (lodash.isArray(this.languageOptions) && this.languageOptions.length > 0) {
        let selectedLanguageId = lodash.get(this.pendingUpdateUser, ['languageId'])
        if (!selectedLanguageId) {
          selectedLanguageId = this.currentUserDefaultLang['id']
        }
        result = lodash.find(this.languageOptions, option => option.id === selectedLanguageId) || this.languageOptions[0]
      }
      return result
    },
    selectedNationalCodeValue () {
      return lodash.get(this.currentNationalCode, 'dial_code') || undefined
    },
    selectedLanguageId () {
      const lang = this.selectedLanguage
      return lang ? lang.id : undefined
    },
    nationalCodeHklOptions () {
      return (this.nationalCodeOptions || []).map((country) => ({
        name: country.dial_code,
        value: country.dial_code,
      }))
    },
    languageHklOptions () {
      return (this.languageOptions || []).map((opt) => ({
        name: this.customLangLabel(opt),
        value: opt.id,
      }))
    },
    isEdit () {
      return this.status === 'edit'
    },
    isValidEmail () {
      return regex.email.test(this.primaryContactEmail) || !this.primaryContactEmail
    },
    isValidPhone () {
      const phoneNumber = lodash.get(this.primaryContactPhoneNumber, ['phoneNumber'])
      return regex.phone.test(phoneNumber) || !phoneNumber
    },
  },
  methods: {
    ...mapActions('user', ['reloadUser']),
    async initUser () {
      const profileData = await getExternalUserProfile()
      this.profileData = profileData
      this.initCurrentData()
    },
    cancel () {
      formUtils.resetDirty()
      this.initCurrentData()
    },
    async saveProfile () {
      if (!this.isValidEmail || !this.isValidPhone) {
        return false
      }
      formUtils.resetDirty()
      try {
        await updateExternalUserProfile(this.pendingUpdateUser)
        this.profileData = this.pendingUpdateUser
        this.initCurrentData()
        notification.fireNotification(true, this.$i18n.t('success'))
      } catch (error) {
        notification.handleResponseError(error)
      }
    },
    initCurrentData () {
      this.pendingUpdateUser = lodash.cloneDeep(this.profileData)
      const { nationalCode, phoneNumber } = getDefaultProfilePhoneNumber(this.pendingUpdateUser)
      this.currentNationalCode = nationalCode
      this.currentPhoneNumber = phoneNumber
    },
    async resetPassword () {
      const username = lodash.get(this.pendingUpdateUser, ['username'])
      const domain = lodash.get(this.pendingUpdateUser, ['domain'])
      await resetExternalUserPassword(username, domain)
    },
    updateContactEmail (newEmail) {
      updateUserProfileEmail(this.pendingUpdateUser, newEmail)
    },
    updateContactPhoneNumber (nationalCodeObj, phoneNumber) {
      const nationalCode = lodash.get(nationalCodeObj, ['dial_code'], '')
      const newPhoneNumber = getFullPhoneNumber(nationalCode, phoneNumber)
      updateUserProfilePhoneNumber(this.pendingUpdateUser, newPhoneNumber)
    },
    onNationalCodeChange (dialCode) {
      const country = lodash.find(this.nationalCodeOptions, (c) => c.dial_code === dialCode)
      if (country) {
        this.updateNationalCode(country)
      }
    },
    onLanguageChange (languageId) {
      const lang = lodash.find(this.languageOptions, (opt) => opt.id === languageId)
      if (lang) {
        this.updateLanguage(lang)
      }
    },
    updateLanguage (newLanguage) {
      this.pendingUpdateUser.languageId = lodash.toNumber(newLanguage.id)
    },
    updatePhoneNumber (newPhoneNumber) {
      this.currentPhoneNumber = newPhoneNumber
      this.updateContactPhoneNumber(this.currentNationalCode, newPhoneNumber)
    },
    updateNationalCode (newCode) {
      this.currentNationalCode = newCode
      this.updateContactPhoneNumber(newCode, this.currentPhoneNumber)
    },
    customLangLabel (option) {
      return this.$t(`langOptions.${option.name}`)
    },
  },
  async mounted () {
    await this.initUser()
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';

.profile-container {
  position: relative;
  background: #FBFAFC;
  .profile-item {
    min-height: 65px;
    label {
      margin-top: 0;
    }
  }
}
.page-header-container {
  color: #0c3a58;
  font-family: Georgia;
  padding: 26px 15px 18px 15px;
  background: white;
  box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
}

.page-header .action-btns {
  float: right;
}

.page-title {
  font-size: 32px;
  color: #0c2b46;
}

.page-header,
.profile-main {
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
}

.profile-main {
  display: flex;
  min-height: 520px;
  margin: 30px auto;
  background: #f8f8f8;
}

.profile-basic,
.profile-account,
.profile-dashboard {
  background: white;
  padding: 24px;
  box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
}
.profile-basic {
  flex: 1;
}
.profile-account-and-dashboard {
  flex: 0.6;
  display: flex;
  flex-direction: column;
  margin-left: 24px;
}
.profile-account {
  flex: 1;
  .profile-item {
    margin-bottom: 20px;
  }
}
.profile-dashboard {
  flex: 0.8;
  margin-top: 24px;
}

.profile-section-title {
  font-weight: bold;
  min-height: 40px;
}

.profile-section-content {
  display: flex;
  justify-content: space-between;
}

.phone-field {
  display: flex;
  justify-content: space-between;

  & .national-code__selector {
    width: 150px;
  }

  & .phone-number__input {
    margin-left: 15px;
    padding-right: 9px;
    flex: 1;
  }
}

span.label-tip {
  font-weight: 300;
}
</style>
